import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Text, Group, Alert, Center, Loader, Badge } from '@mantine/core';
import { IconArrowRight, IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import useSchool from '../../../hooks/useSchool';
import useSchoolStudents from '../../../hooks/useSchoolStudents';
import StudentSidebar from '../../../components/School/StudentSidebar';
import StudentForm from '../../../components/School/StudentForm';
import { getStudentVariations, getPastSeasonStudents, studentKey, carryOverDetails } from '../../../services/schoolService';
import CarryOverModal from '../../../components/School/CarryOverModal';
import { postApi } from '../../../lib/api';

const VIEWPORT_HEIGHT = 'calc(100vh - 140px)'; // account for header + stepper

const SchoolDashboard = ({ readOnly = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { school, currentSeason } = useSchool();

  // Each student's ensembles, shown read-only on their form (edited on the
  // Ensembles step). registrationId → [team names].
  const [ensemblesByStudent, setEnsemblesByStudent] = useState({});
  useEffect(() => {
    if (!currentSeason?.id) return;
    postApi('/api/ensemble', { action: 'schoolStatus', schoolSeasonId: currentSeason.id })
      .then(({ ensembles }) => {
        const map = {};
        ensembles.forEach(e => e.members.forEach(m => { (map[m.registrationId] ||= []).push(e.name); }));
        setEnsemblesByStudent(map);
      })
      .catch(err => console.warn('Could not load ensembles:', err));
  }, [currentSeason?.id]);
  const {
    students, loading, selectedStudentId, setSelectedStudentId,
    addStudent, saveStudent, deleteStudent, saveVariations, updateStudentConsentStatus
  } = useSchoolStudents(currentSeason?.id, currentSeason?.competition_year);

  // Roster carry-over: students from this school's earlier seasons.
  const [pastStudents, setPastStudents] = useState([]);
  const [carryOverOpen, setCarryOverOpen] = useState(false);
  useEffect(() => {
    if (!school?.id || !currentSeason?.competition_year || readOnly) return;
    getPastSeasonStudents(school.id, currentSeason.competition_year)
      .then(setPastStudents)
      .catch(err => console.warn('Could not load last year\'s students:', err));
  }, [school?.id, currentSeason?.competition_year, readOnly]);
  const currentKeys = new Set(students.map(st => studentKey(st.dancer_information)));

  // One at a time, so the modal can show progress and a failure stops cleanly.
  const handleCarryOver = async (chosen, onProgress) => {
    for (let i = 0; i < chosen.length; i++) {
      const id = await addStudent();
      if (!id) throw new Error(`Couldn't add ${chosen[i].dancerInfo.first_name}. Please try again.`);
      const ok = await saveStudent(id, carryOverDetails(chosen[i].dancerInfo));
      if (!ok) throw new Error(`Added ${chosen[i].dancerInfo.first_name}, but couldn't copy their details.`);
      onProgress(i + 1);
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileView, setMobileView] = useState('sidebar'); // 'sidebar' | 'form'
  const [studentVariations, setStudentVariations] = useState({});
  const [optimisticNames, setOptimisticNames] = useState({}); // { [registrationId]: { first_name, last_name } }
  const [navigating, setNavigating] = useState(false);
  const formRef = useRef(null);

  // 'required' = a minor whose consent email has not gone out. See
  // database/2026-09-18-parent-consent-required-state.sql for why this state
  // had to exist before it could be surfaced anywhere.
  const needsConsent = students.filter(s => s.parent_consent_status === 'required');
  const nameOf = (s) => [s.dancer_information?.first_name, s.dancer_information?.last_name]
    .filter(Boolean).join(' ') || 'Unnamed student';

  const flushAndNavigate = async (to) => {
    setNavigating(true);
    try {
      if (formRef.current?.flush) await formRef.current.flush();
    } catch (err) {
      console.error('Flush before navigate failed:', err);
    } finally {
      setNavigating(false);
      navigate(to);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // URL-based auto-select (from confirmation page click-through)
  useEffect(() => {
    const openStudentId = searchParams.get('openStudent');
    if (openStudentId) {
      setSelectedStudentId(openStudentId);
      if (isMobile) setMobileView('form');
    }
  }, [searchParams, setSelectedStudentId, isMobile]);

  // Load variations for selected student
  useEffect(() => {
    if (selectedStudentId && !studentVariations[selectedStudentId]) {
      getStudentVariations(selectedStudentId).then(vars => {
        setStudentVariations(prev => ({ ...prev, [selectedStudentId]: vars }));
      }).catch(() => {});
    }
  }, [selectedStudentId, studentVariations]);

  const handleAddStudent = async () => {
    const newId = await addStudent();
    if (newId && isMobile) setMobileView('form');
  };

  const handleSelectStudent = (id) => {
    setSelectedStudentId(id);
    if (isMobile) setMobileView('form');
  };

  const handleSaveStudent = async (registrationId, studentData) => {
    await saveStudent(registrationId, studentData);
  };

  const handleSaveVariations = async (registrationId, variations) => {
    await saveVariations(registrationId, variations);
    setStudentVariations(prev => ({ ...prev, [registrationId]: variations }));
  };

  const handleNameChange = (registrationId, firstName, lastName) => {
    setOptimisticNames(prev => ({ ...prev, [registrationId]: { first_name: firstName, last_name: lastName } }));
  };

  // The server builds the email from the roster and sets parent_consent_status.
  const handleParentConsentNeeded = async (registrationId, parentEmail) => {
    try {
      const result = await postApi('/api/send-parent-consent-email', { registrationId, parentEmail });
      updateStudentConsentStatus(registrationId, result.alreadyConsented ? 'received' : 'pending');
    } catch (err) {
      console.error('Failed to send parent consent email:', err);
    }
  };

  const selectedStudent = students.find(s => s.id === selectedStudentId);

  if (loading) {
    return (
      <Center style={{ minHeight: '60vh' }}>
        <Loader color="var(--primary-gold)" />
      </Center>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: VIEWPORT_HEIGHT }}>
      <CarryOverModal
        opened={carryOverOpen}
        onClose={() => setCarryOverOpen(false)}
        pastStudents={pastStudents}
        currentKeys={currentKeys}
        year={currentSeason?.competition_year}
        onAdd={handleCarryOver}
      />
      {/* Top bar */}
      <div style={{
        padding: '0.75rem 1.5rem',
        borderBottom: '1px solid #eee',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => flushAndNavigate('/register/school/setup')}
          size="sm"
          loading={navigating}
        >
          {readOnly ? 'View Studio Info' : 'Edit Studio Info'}
        </Button>
        <div style={{ textAlign: 'center' }}>
          <Text fw={700} size="lg" color="var(--charcoal)">{school?.name}</Text>
          <Text size="xs" color="dimmed">
            {school?.contact_first_name} {school?.contact_last_name}
          </Text>
        </div>
        <Group>
          <Badge color="gold" variant="light" size="lg">
            {students.length} student{students.length !== 1 ? 's' : ''}
          </Badge>
          <Button
            rightSection={<IconArrowRight size={16} />}
            onClick={() => flushAndNavigate('/register/school/ensembles')}
            style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
            size="sm"
            loading={navigating}
          >
            {readOnly ? 'View Ensembles' : 'Next: Ensembles'}
          </Button>
        </Group>
      </div>

      {/* Minors with no consent email sent yet. This is the case that went
          unnoticed for all of 2026 — there was no banner for it at all. */}
      {needsConsent.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light" radius={0}>
          <Text size="sm" style={{ textAlign: 'left' }}>
            <strong>{needsConsent.length} student{needsConsent.length !== 1 ? 's' : ''} under 18 still need a parent consent email.</strong>{' '}
            Open each one and use <em>Send Consent Email</em> in the Parent / Guardian section
            {needsConsent.length <= 6 && `: ${needsConsent.map(nameOf).join(', ')}`}.
          </Text>
        </Alert>
      )}

      {/* Pending consent banner */}
      {students.some(s => s.parent_consent_status === 'pending') && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" radius={0}>
          <Text size="sm">
            {students.filter(s => s.parent_consent_status === 'pending').length} student(s) have pending parent consent.
            Consent emails have been sent — waiting for parent confirmation.
          </Text>
        </Alert>
      )}

      {/* Main layout */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Mobile: show sidebar or form */}
        {isMobile ? (
          mobileView === 'sidebar' ? (
            <StudentSidebar
              students={students}
              selectedStudentId={selectedStudentId}
              onSelectStudent={handleSelectStudent}
              onAddStudent={handleAddStudent}
              onAddFromLastYear={pastStudents.length ? () => setCarryOverOpen(true) : undefined}
              onDeleteStudent={deleteStudent}
              studentVariations={studentVariations}
              optimisticNames={optimisticNames}
              isFinalized={readOnly}
              isMobile={true}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '0.5rem 1rem', borderBottom: '1px solid #eee', background: '#fafafa' }}>
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  onClick={() => setMobileView('sidebar')}
                  color="gray"
                >
                  Back to students
                </Button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {selectedStudent ? (
                  <StudentForm
                    ref={formRef}
                    key={selectedStudentId}
                    registrationId={selectedStudentId}
                    initialData={selectedStudent.dancer_information}
                    initialVariations={studentVariations[selectedStudentId]}
                    school={school}
                    consentStatus={selectedStudent.parent_consent_status}
                    onSave={handleSaveStudent}
                    onNameChange={handleNameChange}
                    onParentConsentNeeded={handleParentConsentNeeded}
                    saveVariationsFn={handleSaveVariations}
                    ensembleNames={ensemblesByStudent[selectedStudentId] || []}
                    pastStudents={pastStudents}
                    isFinalized={readOnly}
                  />
                ) : (
                  <Center style={{ height: '100%' }}>
                    <Text color="dimmed">No student selected.</Text>
                  </Center>
                )}
              </div>
            </div>
          )
        ) : (
          /* Desktop: side-by-side */
          <>
            <StudentSidebar
              students={students}
              selectedStudentId={selectedStudentId}
              onSelectStudent={handleSelectStudent}
              onAddStudent={handleAddStudent}
              onAddFromLastYear={pastStudents.length ? () => setCarryOverOpen(true) : undefined}
              onDeleteStudent={deleteStudent}
              studentVariations={studentVariations}
              optimisticNames={optimisticNames}
              isFinalized={readOnly}
            />
            <div style={{ flex: 1, overflowY: 'auto', background: 'white' }}>
              {selectedStudent ? (
                <StudentForm
                  ref={formRef}
                  key={selectedStudentId}
                  registrationId={selectedStudentId}
                  initialData={selectedStudent.dancer_information}
                  initialVariations={studentVariations[selectedStudentId]}
                  school={school}
                  consentStatus={selectedStudent.parent_consent_status}
                  onSave={handleSaveStudent}
                  onNameChange={handleNameChange}
                  onParentConsentNeeded={handleParentConsentNeeded}
                  saveVariationsFn={handleSaveVariations}
                  ensembleNames={ensemblesByStudent[selectedStudentId] || []}
                  pastStudents={pastStudents}
                  isFinalized={readOnly}
                />
              ) : (
                <Center style={{ height: '100%' }}>
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Text size="xl" color="dimmed" mb="xs">
                      {students.length === 0 ? 'No students yet' : 'Select a student'}
                    </Text>
                    <Text color="dimmed" size="sm">
                      {students.length === 0
                        ? 'Click "Add Student" to register your first student.'
                        : 'Click a student in the sidebar to view or edit their information.'}
                    </Text>
                  </div>
                </Center>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SchoolDashboard;
